import { HttpBackend, HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { filter, firstValueFrom } from 'rxjs';

import { IDENTITY_API_URL_TOKEN, IdentityClient, IdentityClientService, IdentityConfigurationService, IdentityConstants } from '@auth';
import { AppInitValues } from '@constants';
import { getClientUrl, StorageService } from '@core';
import { NotificationService } from '@services';

type ApiError = { error?: { message?: string } | { message?: string }[], message?: string };

@Injectable({
    providedIn: 'root'
})
export class ConfigService {
    private httpBackend = inject(HttpBackend);
    private storageService = inject(StorageService);
    private http = inject(HttpClient);
    private notification = inject(HotToastService);
    private notificationService = inject(NotificationService);
    private identityConfigService = inject(IdentityConfigurationService);
    private identityClientService = inject(IdentityClientService);
    private identityUrl = inject(IDENTITY_API_URL_TOKEN);
    private storageKeys = inject(IdentityConstants).identityConstants;

    // Tracks which x-service was last loaded, so getModuleConfig() can skip re-fetching
    // service/configuration when the target module matches what loadConfig() already loaded.
    private lastFetchedServiceCode: string | null = null;

    // Concurrent getModuleConfig() calls for the same module share one request.
    private pendingModuleConfig: { serviceCode: string, request: Promise<void> } | null = null;

    async loadConfig(): Promise<void> {
        const client = getClientUrl();

        let headers = new HttpHeaders({ 'X-Product': AppInitValues.PRODUCT, 'X-Service': AppInitValues.INITIAL_SERVICE });

        let clientInfo: HttpResponse<IdentityClient>;

        try {
            // The fetch backend emits a Sent event before the response, so wait for the response itself.
            clientInfo = await firstValueFrom(
                this.httpBackend.handle(new HttpRequest('GET', `${this.identityUrl}/api/client/get?url=${client}`, { headers }))
                    .pipe(filter((event): event is HttpResponse<IdentityClient> => event instanceof HttpResponse))
            );
        }
        catch (err) {
            this.notification.error(this.getApiErrorMessage(err));
            throw err;
        }

        const clientCode = clientInfo.body?.code ?? null;

        if (clientCode === null) {
            const message = `Client not found: ${client}`;

            this.notification.error(message);
            throw new Error(message);
        }

        const currentClient = this.identityClientService.get();

        if (Object.keys(currentClient).length === 0 || currentClient.code !== clientCode) {
            this.storageService.set(this.storageKeys.configurationClientKey, clientCode);
            this.storageService.set(this.storageKeys.currentClientKey, JSON.stringify(clientInfo.body));
        }
        this.storageService.set(this.storageKeys.configurationProductKey, AppInitValues.PRODUCT);
        this.storageService.set(this.storageKeys.configurationServiceKey, AppInitValues.INITIAL_SERVICE);

        headers = headers.append('X-Client', clientCode);

        let clientConfig: unknown;
        let serviceConfig: unknown[];

        try {
            [clientConfig, serviceConfig] = await Promise.all([
                firstValueFrom(this.http.get<unknown>(`${this.identityUrl}/api/client/configuration`, { headers })),
                firstValueFrom(this.http.get<unknown[]>(`${this.identityUrl}/api/service/configuration`, { headers }))
            ]);
        }
        catch (err) {
            this.notificationService.error(err, 'UNHANDLED_ERROR');
            console.error('Error loading configuration:', err);
            throw err;
        }

        if (!serviceConfig?.length) {
            const message = `HUB service not configured for: ${clientInfo.body?.name}`;

            this.notification.error(message);
            throw new Error(message);
        }

        this.storageService.set(this.storageKeys.currentClientConfigurationKey, JSON.stringify(clientConfig));
        this.storeServiceConfiguration(serviceConfig, AppInitValues.INITIAL_SERVICE);
    }

    // Always settles: callers (moduleResolver) wait on it, so a failure must reject instead of leaving the navigation pending.
    getModuleConfig(): Promise<void> {
        // Reuse the config already loaded by loadConfig() when the target module (set by
        // moduleResolver right before this call) matches what was last fetched.
        const targetServiceCode = this.identityConfigService.getService();

        if (targetServiceCode && targetServiceCode === this.lastFetchedServiceCode) {
            return Promise.resolve();
        }

        if (this.pendingModuleConfig?.serviceCode === targetServiceCode) {
            return this.pendingModuleConfig.request;
        }

        const request = this.fetchModuleConfig(targetServiceCode).finally(() => {
            this.pendingModuleConfig = null;
        });

        this.pendingModuleConfig = { serviceCode: targetServiceCode, request };

        return request;
    }

    private async fetchModuleConfig(serviceCode: string): Promise<void> {
        const clientCode = this.identityClientService.get()?.code ?? null;

        if (clientCode === null) {
            const message = 'No client found in storage for module configuration';

            this.notification.error(message);
            throw new Error(message);
        }

        try {
            const data = await firstValueFrom(this.http.get<unknown[]>(`${this.identityUrl}/api/service/configuration`));

            this.storeServiceConfiguration(data, serviceCode);
        }
        catch (err) {
            this.notificationService.error(err, 'UNHANDLED_ERROR');
            console.error('Error loading configuration:', err);
            throw err;
        }
    }

    // err.error is not an object on network failures, so read it defensively.
    private getApiErrorMessage(err: unknown): string {
        const { error: body, message } = (err ?? {}) as ApiError;

        return (Array.isArray(body) ? body[0]?.message : body?.message) ?? message ?? 'Unable to load the configuration';
    }

    private storeServiceConfiguration(data: unknown[], serviceCode: string): void {
        this.storageService.set(this.storageKeys.currentServiceConfigurationKey, JSON.stringify(data));
        this.lastFetchedServiceCode = serviceCode;
    }
}
